import type { FasciaSizeParams, RidgeRailAtPeakParams, RoofSlopesParams } from "building-types/framing/framingTypes";
import { LumberProfileType } from "building-types/framing/lumberTypes";
import { LumberProfileCatalog } from "building-types/framing/lumberTypes";
import {
  DEFAULT_ROOF_FRAMING_SPACING,
  DEFAULT_WALL_FRAMING_OFFSET,
  MIN_ROOF_FRAMING_SPACING,
  TRUSS_THICKNESS,
} from "building-types/framing/roofFramingConstants";
import type { FramingPlaneConstraint, GeneratedRoofFramingPlane } from "building-types/framing/roofFramingTypes";
import {
  FramingPlaneConstraintType,
  HipFramingMethod,
  RoofFramingPlaneType,
  RoofFramingSpacingMethod,
  RoofFramingType,
  type GeneratedRoofFraming,
  type RoofFraming,
  type RoofTruss,
} from "building-types/framing/roofFramingTypes";
import type { RoofTrussBasicInput } from "building-types/framing/roofTrussTypes";
import { RoofTrussType } from "building-types/framing/roofTrussTypes";
import type { GeneratedRoofSystem } from "building-types/roofs/generatedRoofSystemTypes";
import { RoofTruncationType } from "building-types/settings/roofBaseTypes";
import type { RidgeRail } from "building-types/settings/roofTypes";
import { GableRoofType } from "building-types/settings/settingsTypesEnum";
import type { RWTable } from "common/data/table";
import type { UnsignedNumber } from "common/types/nominalNumbersTypes";
import { unsignedNumberNew } from "common/utils/nominalNumbers";
import { buildingEntityBaseNew } from "doc/utils/buildingEntityTools";
import { DIST_EPSILON } from "geom/core";
import {
  planeCopy,
  planeCopyMoveAlongNormal,
  planeCopyReversed,
  planeDistanceTo,
  planeNewXY,
  planeNewXZ,
  planeReverse,
  PlaneSide,
  vec3PlaneSide,
} from "geom/plane";
import type { Plane } from "geom/planeTypes";
import type { PolyLine2 } from "geom/polyLine2Types";
import type { PolyLine3 } from "geom/polyLine3";
import { polyLine3Copy, polyLine3ExtendBothEnds, polyLine3New } from "geom/polyLine3";
import type { Vec2 } from "geom/vec2";
import { vec2Dist } from "geom/vec2";
import { vec3Cross, vec3Dist, vec3Normalize } from "geom/vec3";
import { ChordTrussGenerator } from "../structural-framing/roof/chordTrussGenerator";
import { FinkTrussGenerator } from "../structural-framing/roof/finkTrussGenerator";
import { FlyTrussGenerator } from "../structural-framing/roof/flyTrussGenerator";
import { calculateHipSpans } from "../structural-framing/roof/hipFramingUtils";
import { resolvedTruncationHipSideSlopeForFraming } from "../structural-framing/roof/hipJackSlopeFromTruncation";
import { HipTrussGenerator } from "../structural-framing/roof/hipTrussGenerator";
import { HoweTrussGenerator } from "../structural-framing/roof/howeTrussGenerator";
import {
  generateJackRafterAt,
  generateJackRafters,
  generateJackRaftersForHipTrusses,
} from "../structural-framing/roof/jackFramingUtils";
import { KingPostTrussGenerator } from "../structural-framing/roof/kingPostTrussGenerator";
import { QueenPostTrussGenerator } from "../structural-framing/roof/queenPostTrussGenerator";
import {
  generateLookout,
  generateRidgeRafter,
  roofFramingConstraintPoints,
  roofFramingPeakRidgeControlLines,
  roofFramingPlanes,
  roofFramingSpanBuilder,
  roofFramingValleyControlLines,
  roofTopFacesXYPlaneAndNeighbors,
} from "../structural-framing/roof/roofFramingUtils";
import { ScissorTrussGenerator } from "../structural-framing/roof/scissorTrussGenerator";
import { StickFrameTrussGenerator } from "../structural-framing/roof/stickFrameTrussGenerator";
import { trussBasicInputNew } from "../structural-framing/roof/trussUtils";

interface BuildRoofFramingOptions {
  readonly roofSystem: GeneratedRoofSystem;
  readonly roofFramingTableNew: RWTable<GeneratedRoofFraming>;
}

export function roofFramingNew(
  roofSystemId: string,
  subRoofId: string,
  framing: RoofFraming[],
  levelId: string,
  clippedRoofTopFacesXY: PolyLine2[],
): GeneratedRoofFraming {
  return {
    ...buildingEntityBaseNew("ROOF_FRAMING", subRoofId, {
      type: "ROOF",
      id: subRoofId,
    }),
    roofSystemId,
    levelId,
    framing,
    clippedRoofTopFacesXY,
  };
}

export function buildRoofFraming({ roofSystem, roofFramingTableNew }: BuildRoofFramingOptions) {
  const { roofs: subRoofs, id } = roofSystem;

  // These conditions are evaluated per sub-roof and buildRoofFraming runs repeatedly within a
  // single generation, so warning inline repeats the same message once per roof per pass. Group
  // the affected roof ids by reason and emit one line per reason after the loop.
  const framingWarnings = new Map<string, string[]>();
  const noteFramingWarning = (reason: string, subRoofId: string): void => {
    const affected = framingWarnings.get(reason);
    if (affected) {
      affected.push(subRoofId);
      return;
    }
    framingWarnings.set(reason, [subRoofId]);
  };

  const defaultFramingInput = trussBasicInputNew({
    trussType: RoofTrussType.CHORD_TRUSS,
    leftSpan: unsignedNumberNew(73.75),
    leftEaveFasciaSize: unsignedNumberNew(8),
    rightSpan: unsignedNumberNew(73.75),
    rightEaveFasciaSize: unsignedNumberNew(8),
    leftOverhang: unsignedNumberNew(12),
    rightOverhang: unsignedNumberNew(12),
    leftBearingWidth: unsignedNumberNew(3.5),
    rightBearingWidth: unsignedNumberNew(3.5),
    bottomChordOffset: 0,
    leftTopChordPitch: unsignedNumberNew(1),
    rightTopChordPitch: unsignedNumberNew(1),
    topChordProfile: LumberProfileType.SPF_2X4,
    bottomChordProfile: LumberProfileType.SPF_2X4,
    webChordProfile: LumberProfileType.SPF_2X4,
    ridgeRafterProfile: LumberProfileType.SPF_2X4,
    trussPlane: planeNewXZ(),
  });
  const {
    roofTopFacesUnionedXYPlanes: cachedRoofTopFacesUnionedXYPlanesTable,
    roofTopFacesDCEL: cachedRoofTopFacesDCEL,
    polygonRoofIdMapping: cachedPolygonRoofIdMapping,
  } = roofTopFacesXYPlaneAndNeighbors(subRoofs);

  // build the trusses for all sub roofs
  for (const subRoofId in subRoofs) {
    const subRoof = subRoofs[subRoofId];

    const { roofSettings, roofType } = subRoof;
    const { generateRoofFraming } = roofSettings;

    if (roofType !== GableRoofType.DEFAULT) {
      noteFramingWarning(`roof framing is not supported for ${roofType}`, subRoofId);
      continue;
    }

    if (!generateRoofFraming) {
      continue;
    }
    const clippedRoofTopFacesXY = cachedRoofTopFacesUnionedXYPlanesTable[subRoofId];

    if (clippedRoofTopFacesXY.length === 0) {
      noteFramingWarning("no top faces to generate framing from", subRoofId);
      continue;
    }

    const valleyControlLines = roofFramingValleyControlLines(
      subRoofId,
      cachedRoofTopFacesDCEL[subRoofId],
      cachedPolygonRoofIdMapping[subRoofId],
      subRoofs,
    );

    //build the function to compute the span at each point in the clipped roof top faces
    const computeSpanAt: (x: number, y: number, point: Vec2) => number = roofFramingSpanBuilder(clippedRoofTopFacesXY);

    let {
      trussType = RoofTrussType.CHORD_TRUSS,
      roofFramingSpacing = DEFAULT_ROOF_FRAMING_SPACING,
      hipFramingMethod,
    } = roofSettings;

    if (roofFramingSpacing < MIN_ROOF_FRAMING_SPACING) {
      roofFramingSpacing = MIN_ROOF_FRAMING_SPACING;
      noteFramingWarning(`framing spacing below the minimum of ${MIN_ROOF_FRAMING_SPACING}, clamped to it`, subRoofId);
    }

    const {
      roofFramingSpacingMethod = RoofFramingSpacingMethod.EQUAL_SPACING_BEGIN_END,
      ceilingJoistOffset = 0,
      topChordProfile = LumberProfileType.SPF_2X4,
      bottomChordProfile = LumberProfileType.SPF_2X4,
      webChordProfile = LumberProfileType.SPF_2X4,
      ridgeRafterProfile = LumberProfileType.SPF_2X4,
      includeFlyRafters: includeFlyRaftersFromRoofSettings = true,
      flyRafterProfile = LumberProfileType.SPF_2X4,
      includeLookoutRafters: includeLookoutRaftersFromRoofSettings = true,
      lookoutRafterProfile = LumberProfileType.SPF_2X4,
      lookoutRafterSpacing = 24,
      roofFramingType = RoofFramingType.RAFTER,
      includeStickFramingAtStart: includeStickFramingAtStartFromRoofSettings = true,
      includeStickFramingAtEnd: includeStickFramingAtEndFromRoofSettings = true,
      webChordSpacingAtStart = 16,
      webChordSpacingAtEnd = 16,
      stickFramingTopChordProfileAtStart = LumberProfileType.SPF_2X4,
      stickFramingBottomChordProfileAtStart = LumberProfileType.SPF_2X4,
      stickFramingWebChordProfileAtStart = LumberProfileType.SPF_2X4,
      stickFramingTopChordProfileAtEnd = LumberProfileType.SPF_2X4,
      stickFramingBottomChordProfileAtEnd = LumberProfileType.SPF_2X4,
      stickFramingWebChordProfileAtEnd = LumberProfileType.SPF_2X4,
      jackRafterProfile = LumberProfileType.SPF_2X4,
      eaveFasciaSize = 8, //TODO: this needs to come from the roof side
      setBackForStepDownTruss = 72,
    } = roofSettings;
    const { levelId, roofVolume } = subRoof;
    const {
      rails,
      leftSpan,
      rightSpan,
      startTruncation,
      endTruncation,
      rectangularOverhangs,
      leftSideSlopes,
      rightSideSlopes,
      neighborRoofs,
    } = roofVolume;

    const hasStartNeighbor: boolean =
      neighborRoofs.startNeighbor.roofId !== undefined && subRoofs[neighborRoofs.startNeighbor.roofId] !== undefined;
    const hasEndNeighbor: boolean =
      neighborRoofs.endNeighbor.roofId !== undefined && subRoofs[neighborRoofs.endNeighbor.roofId] !== undefined;

    const includeStickFramingAtStart: boolean = includeStickFramingAtStartFromRoofSettings && !hasStartNeighbor;
    const includeStickFramingAtEnd: boolean = includeStickFramingAtEndFromRoofSettings && !hasEndNeighbor;

    const includeFlyRaftersAtStart: boolean = includeFlyRaftersFromRoofSettings && !hasStartNeighbor;
    const includeFlyRaftersAtEnd: boolean = includeFlyRaftersFromRoofSettings && !hasEndNeighbor;

    const includeLookoutRaftersAtStart: boolean = includeFlyRaftersAtStart && includeLookoutRaftersFromRoofSettings;
    const includeLookoutRaftersAtEnd: boolean = includeFlyRaftersAtEnd && includeLookoutRaftersFromRoofSettings;

    const { startOverhang, endOverhang, leftOverhang, rightOverhang } = rectangularOverhangs;
    const { peakRail, ridgeRails } = rails;

    if (!hipFramingMethod) {
      hipFramingMethod =
        roofFramingType === RoofFramingType.RAFTER
          ? HipFramingMethod.JACK_RAFTERS
          : HipFramingMethod.STEP_DOWN_HIP_FRAMING;
    }

    const framingPlaneThickness = LumberProfileCatalog[topChordProfile]?.height ?? TRUSS_THICKNESS;
    let stickFramingAtStartAdditionalPlaneOffset: number = 0;
    let stickFramingAtEndAdditionalPlaneOffset: number = 0;
    if (includeStickFramingAtStart) {
      stickFramingAtStartAdditionalPlaneOffset =
        DEFAULT_WALL_FRAMING_OFFSET +
        0.5 * framingPlaneThickness -
        (LumberProfileCatalog[stickFramingBottomChordProfileAtStart]?.width ?? TRUSS_THICKNESS);
    }
    if (includeStickFramingAtEnd) {
      stickFramingAtEndAdditionalPlaneOffset = -(DEFAULT_WALL_FRAMING_OFFSET - 0.5 * framingPlaneThickness);
    }

    if (roofFramingType === RoofFramingType.RAFTER) {
      trussType = RoofTrussType.FLY_TRUSS;
    }

    const leftTopChordPitch = leftSideSlopes[0].slope;
    const rightTopChordPitch = rightSideSlopes[0].slope;
    let discontinueFraming: boolean = false;
    for (const { slope } of leftSideSlopes) {
      if (Math.abs(slope - leftTopChordPitch) > DIST_EPSILON) {
        noteFramingWarning("roof framing is not supported for multiple slope roofs", subRoofId);
        discontinueFraming = true;
        break;
      }
    }
    for (const { slope } of rightSideSlopes) {
      if (Math.abs(slope - rightTopChordPitch) > DIST_EPSILON) {
        noteFramingWarning("roof framing is not supported for multiple slope roofs", subRoofId);
        discontinueFraming = true;
        break;
      }
    }
    if (discontinueFraming) {
      continue;
    }
    let startOffset = 0;
    let endOffset = 0;
    let hasStartTruncation = true;
    let hasEndTruncation = true;
    let startHipSideSlope: number | undefined;
    let endHipSideSlope: number | undefined;

    if (
      !startTruncation ||
      startTruncation.type === RoofTruncationType.NONE ||
      (startTruncation.type === RoofTruncationType.TRUNCATED &&
        startTruncation.normalizedHeight &&
        Math.abs(startTruncation.heightFactor - 1) < DIST_EPSILON)
    ) {
      startOffset = -(startOverhang - 0.5 * framingPlaneThickness);
      hasStartTruncation = false;
    } else {
      const isStartTruncation = true;
      startHipSideSlope = resolvedTruncationHipSideSlopeForFraming(startTruncation, neighborRoofs, isStartTruncation);
    }
    if (
      !endTruncation ||
      endTruncation.type === RoofTruncationType.NONE ||
      (endTruncation.type === RoofTruncationType.TRUNCATED &&
        endTruncation.normalizedHeight &&
        Math.abs(endTruncation.heightFactor - 1) < DIST_EPSILON)
    ) {
      endOffset = -(endOverhang - 0.5 * framingPlaneThickness);
      hasEndTruncation = false;
    } else {
      const isStartTruncation = false;
      endHipSideSlope = resolvedTruncationHipSideSlopeForFraming(endTruncation, neighborRoofs, isStartTruncation);
    }

    const peakRidgeControlLines = roofFramingPeakRidgeControlLines(clippedRoofTopFacesXY, peakRail);

    if (peakRidgeControlLines.length > 1 || peakRidgeControlLines.length === 0) {
      //TODO: handle multiple peak ridge control lines-this can happen [1] if the peak ridge is not a straight line, [2] if the roof is a complex shape (generated via clip to perimeter option)
      noteFramingWarning("roof framing is not fully supported with multiple or no peak ridge control lines", subRoofId);
    }
    if (peakRidgeControlLines.length === 0) {
      continue;
    }
    const peakRailExtended = polyLine3ExtendBothEnds(polyLine3Copy(peakRail), -framingPlaneThickness, 0);
    const lastIndex = peakRidgeControlLines.length - 1;
    const peakRidgeControlLineStartEnd = polyLine3New([
      peakRidgeControlLines[0][0],
      peakRidgeControlLines[0][1],
      peakRidgeControlLines[0][2],
      peakRidgeControlLines[lastIndex][3],
      peakRidgeControlLines[lastIndex][4],
      peakRidgeControlLines[lastIndex][5],
    ]);

    const peakRailNoExtensionExtended = polyLine3ExtendBothEnds(
      polyLine3Copy(peakRidgeControlLineStartEnd),
      -framingPlaneThickness,
      0,
    );

    const lengthOfPeakRailNoExtension = vec3Dist(
      peakRailNoExtensionExtended[0],
      peakRailNoExtensionExtended[1],
      peakRailNoExtensionExtended[2],
      peakRailNoExtensionExtended[3],
      peakRailNoExtensionExtended[4],
      peakRailNoExtensionExtended[5],
    );

    const [peakRailExtendedNormX, peakRailExtendedNormY, peakRailExtendedNormZ] = vec3Normalize(
      peakRailExtended[0] - peakRailExtended[3],
      peakRailExtended[1] - peakRailExtended[4],
      peakRailExtended[2] - peakRailExtended[5],
    );

    const constraints: FramingPlaneConstraint[] = [];
    if (roofFramingType === RoofFramingType.TRUSS) {
      if (hasStartTruncation) {
        constraints.push({
          distanceFromStart: setBackForStepDownTruss,
          type: FramingPlaneConstraintType.START_STEP_DOWN_FRAMING,
        });
      }
      if (hasEndTruncation) {
        constraints.push({
          distanceFromStart: lengthOfPeakRailNoExtension - setBackForStepDownTruss,
          type: FramingPlaneConstraintType.END_STEP_DOWN_FRAMING,
        });
      }
    }
    const peakRidgeRailConstraints = roofFramingConstraintPoints(peakRailNoExtensionExtended, clippedRoofTopFacesXY);
    constraints.push(...peakRidgeRailConstraints);

    const framingPlanes = computeFramingPlanes(
      peakRailExtended,
      peakRailNoExtensionExtended,
      startOffset,
      endOffset,
      unsignedNumberNew(roofFramingSpacing),
      roofFramingSpacingMethod,
      constraints,
    );

    const { firstPeakFramingPlaneIndex, lastPeakFramingPlaneIndex } =
      getFirstLastPeakFramingPlaneIndices(framingPlanes);

    const { leftRidgeRail: leftRidgeRailAtFirstPeak, rightRidgeRail: rightRidgeRailAtFirstPeak } =
      getLeftRightRidgeRails(ridgeRails, framingPlanes, true, firstPeakFramingPlaneIndex, framingPlaneThickness);
    const { leftRidgeRail: leftRidgeRailAtLastPeak, rightRidgeRail: rightRidgeRailAtLastPeak } = getLeftRightRidgeRails(
      ridgeRails,
      framingPlanes,
      false,
      lastPeakFramingPlaneIndex,
      framingPlaneThickness,
    );

    let peakRidgeRail = ridgeRails.find(({ isHorizontal }) => {
      if (isHorizontal) {
        return true;
      }
      return false;
    })?.rail;

    if (peakRidgeRail) {
      peakRidgeRail = polyLine3Copy(peakRidgeRail);
    } else {
      peakRidgeRail = peakRailNoExtensionExtended;
    }

    const trussFramingPlanes = framingPlanes.map((plane) => plane.plane);
    const roofFraming: RoofFraming[] = [];
    const ridgeRailsAtPeak: RidgeRailAtPeakParams = {
      leftRidgeRailAtFirstPeak,
      rightRidgeRailAtFirstPeak,
      leftRidgeRailAtLastPeak,
      rightRidgeRailAtLastPeak,
      peakRidgeRail,
      firstPeakFramingPlaneIndex,
      lastPeakFramingPlaneIndex,
    };

    // Add ridge rafters with end clip planes
    addRidgeRaftersToRoofFraming({
      roofFraming,
      leftTopChordPitch,
      rightTopChordPitch,
      ridgeRafterProfile,
      trussFramingPlanes,
      framingPlaneThickness,
      ridgeRailsAtPeak,
    });

    valleyControlLines.forEach((valleyControlLine) => {
      roofFraming.push(generateRidgeRafter(valleyControlLine, ridgeRafterProfile));
    });

    if (trussFramingPlanes.length > 3) {
      addLookoutRaftersToRoofFraming({
        roofFraming,
        lookoutRafterProfile,
        computedFramingPlanes: framingPlanes,
        ridgeRailsAtPeak,
        roofFramingSpacing,
        leftSpan,
        rightSpan,
        leftOverhang,
        rightOverhang,
        leftTopChordPitch,
        rightTopChordPitch,
        lookoutRafterSpacing,
        includeLookoutRaftersAtStart,
        includeLookoutRaftersAtEnd,
      });
    }

    // Create appropriate truss generator based on type
    const peakRailTrussGenerator = getTrussGenerator(trussType);

    let stickFrameTrussGenerator: StickFrameTrussGenerator | undefined;
    if (includeStickFramingAtStart || includeStickFramingAtEnd) {
      stickFrameTrussGenerator = new StickFrameTrussGenerator();
    }
    let hipTrussGenerator: HipTrussGenerator | undefined;

    if (hasStartTruncation || hasEndTruncation) {
      hipTrussGenerator = new HipTrussGenerator();
    }

    let flyTrussGenerator: FlyTrussGenerator | undefined;
    if (includeFlyRaftersAtStart || includeFlyRaftersAtEnd) {
      flyTrussGenerator = new FlyTrussGenerator();
    }

    //we need to find the start and end hip framing planes for the hip trusses - this is done by finding the first and last framing planes that are beyond or at
    //the setBackForStepDownTruss. There is always a truss at the setBack distance, this is ascertained by passing in constraint points above
    const { startHipFramingPlaneIndex, endHipFramingPlaneIndex } = hipFramingPlanesStartEndIndices(
      framingPlanes,
      setBackForStepDownTruss,
      setBackForStepDownTruss,
      framingPlaneThickness,
    );

    const fasciaSizes: FasciaSizeParams = {
      startEaveFasciaSize: eaveFasciaSize,
      endEaveFasciaSize: eaveFasciaSize,
      leftEaveFasciaSize: eaveFasciaSize,
      rightEaveFasciaSize: eaveFasciaSize,
    };

    const roofSlopes: RoofSlopesParams = {
      startHipSideSlope,
      endHipSideSlope,
      leftTopChordPitch,
      rightTopChordPitch,
    };

    const startRidgeMidJackPitchEffective = startHipSideSlope ?? leftTopChordPitch ?? rightTopChordPitch;
    const endRidgeMidJackPitchEffective = endHipSideSlope ?? leftTopChordPitch ?? rightTopChordPitch;
    /**
     * The following loop generates the trusses for the roof
     * note: roof rafter framing is generated by leveraging the truss generator of type "FLY_TRUSS",
     * similarly stick framing is generated by leveraging the truss generator of type "STICK_FRAME_TRUSS"
     *
     * jack rafter framing for hip rafters is generated separately in the loop following this one
     *
     */
    framingPlanes.forEach((framingPlane, index) => {
      generateTrussForPlane(framingPlane, index, {
        trussBasicInput: defaultFramingInput,
        stickFrameTrussGenerator,
        flyTrussGenerator,
        peakRailTrussGenerator,
        hipTrussGenerator,
        includeFlyRaftersAtStart: includeFlyRaftersAtStart ?? false,
        includeFlyRaftersAtEnd: includeFlyRaftersAtEnd ?? false,
        hipFramingMethod,
        leftSpan,
        rightSpan,
        leftOverhang,
        rightOverhang,
        ceilingJoistOffset,
        ridgeRafterProfile,
        topChordProfile,
        bottomChordProfile,
        webChordProfile,
        flyRafterProfile,
        lookoutRafterProfile,
        start: {
          topChordProfile: stickFramingTopChordProfileAtStart,
          bottomChordProfile: stickFramingBottomChordProfileAtStart,
          webChordProfile: stickFramingWebChordProfileAtStart,
          webChordSpacing: webChordSpacingAtStart,
          additionalPlaneOffset: stickFramingAtStartAdditionalPlaneOffset,
          include: includeStickFramingAtStart ?? false,
        },
        end: {
          topChordProfile: stickFramingTopChordProfileAtEnd,
          bottomChordProfile: stickFramingBottomChordProfileAtEnd,
          webChordProfile: stickFramingWebChordProfileAtEnd,
          webChordSpacing: webChordSpacingAtEnd,
          additionalPlaneOffset: stickFramingAtEndAdditionalPlaneOffset,
          include: includeStickFramingAtEnd ?? false,
        },
        trussType,
        framingPlanes,
        ridgeRailsAtPeak,
        peakRailExtendedNorm: [peakRailExtendedNormX, peakRailExtendedNormY, peakRailExtendedNormZ],
        setBackForStepDownTruss,
        framingPlaneThickness,
        fasciaSizes,
        roofSlopes,
        jackRafterProfile,
        roofFraming,
        startHipFramingPlaneIndex,
        endHipFramingPlaneIndex,
        computeSpanAt,
      });
    });

    //the following loop generates the jack rafters for the roof which is framed using rafters only. The jack rafters or jack trusses are
    //are generated along with the hip trusses when roof is framed using trusses.

    framingPlanes.forEach((framingPlane, index) => {
      if (roofFramingType === RoofFramingType.RAFTER) {
        generateJackRafters(framingPlane, index, roofFraming, {
          framingPlanes,
          jackRafterProfile,
          ridgeRailsAtPeak,
          leftSpan: unsignedNumberNew(
            computeSpanAt(framingPlane.plane.originX, framingPlane.plane.originY, [
              framingPlane.plane.uX,
              framingPlane.plane.uY,
            ]),
          ),
          rightSpan: unsignedNumberNew(
            computeSpanAt(framingPlane.plane.originX, framingPlane.plane.originY, [
              -framingPlane.plane.uX,
              -framingPlane.plane.uY,
            ]),
          ),
          peakRailExtendedNorm: [peakRailExtendedNormX, peakRailExtendedNormY, peakRailExtendedNormZ],
          fasciaSizes,
          roofSlopes,
        });
      }
      if (hipFramingMethod === HipFramingMethod.JACK_RAFTERS) {
        const startRidgeMidJackPitch = startRidgeMidJackPitchEffective;
        const endRidgeMidJackPitch = endRidgeMidJackPitchEffective;
        const generateMidJackRafterAtPeak = ({
          peakFramingPlaneIndex,
          spanReferenceFramingPlaneIndex,
          pitch,
          isEnd,
        }: {
          readonly peakFramingPlaneIndex: number | undefined;
          readonly spanReferenceFramingPlaneIndex: number;
          readonly pitch: number;
          readonly isEnd: boolean;
        }): void => {
          if (!peakFramingPlaneIndex || peakFramingPlaneIndex === 0) {
            return;
          }
          const peakPlane = framingPlanes[peakFramingPlaneIndex].plane;
          const spanReferencePlane = framingPlanes[spanReferenceFramingPlaneIndex].plane;
          const span = vec2Dist(
            spanReferencePlane.originX,
            spanReferencePlane.originY,
            peakPlane.originX,
            peakPlane.originY,
          );
          generateJackRafterAt(
            {
              x: peakPlane.originX,
              y: peakPlane.originY,
              z: peakPlane.originZ,
            },
            span,
            pitch,
            peakPlane,
            true,
            isEnd,
            jackRafterProfile,
            eaveFasciaSize,
            -framingPlaneThickness / 2,
            roofFraming,
          );
        };

        if (hasStartTruncation) {
          //generate start jack rafter in the middle of the first peak framing plane
          generateMidJackRafterAtPeak({
            peakFramingPlaneIndex: firstPeakFramingPlaneIndex,
            spanReferenceFramingPlaneIndex: 0,
            pitch: startRidgeMidJackPitch,
            isEnd: false,
          });
        }
        if (hasEndTruncation) {
          //generate end jack rafter in the middle of the last peak framing plane
          generateMidJackRafterAtPeak({
            peakFramingPlaneIndex: lastPeakFramingPlaneIndex,
            spanReferenceFramingPlaneIndex: framingPlanes.length - 1,
            pitch: endRidgeMidJackPitch,
            isEnd: true,
          });
        }
      }
    });

    //we need to generate jack rafters for the hip trusses
    if (roofFramingType === RoofFramingType.TRUSS && hipFramingMethod !== HipFramingMethod.JACK_RAFTERS) {
      generateJackRaftersForHipTrusses({
        trussBasicInput: defaultFramingInput,
        startHipFramingPlaneIndex,
        endHipFramingPlaneIndex,
        hipFramingMethod,
        roofFraming,
        framingPlanes,
        framingPlaneThickness,
        peakRailExtendedNorm: [peakRailExtendedNormX, peakRailExtendedNormY, peakRailExtendedNormZ],
        fasciaSizes,
        ridgeRailsAtPeak,
        roofSlopes,
        jackRafterProfile,
        roofFramingSpacing,
      });
    }

    roofFramingTableNew[subRoofId] = roofFramingNew(id, subRoofId, roofFraming, levelId, clippedRoofTopFacesXY);
  }

  for (const [reason, affected] of framingWarnings) {
    console.warn(`Roof framing: ${reason} (${affected.length} roof(s): ${affected.join(", ")})`);
  }
}

function computeFramingPlanes(
  peakRail: PolyLine3,
  peakRailFull: PolyLine3,
  startOffset: number,
  endOffset: number,
  roofFramingSpacing: UnsignedNumber,
  roofFramingSpacingMethod: RoofFramingSpacingMethod = RoofFramingSpacingMethod.EQUAL_SPACING_BEGIN_END,
  constraints: FramingPlaneConstraint[] = [],
): GeneratedRoofFramingPlane[] {
  return (
    roofFramingPlanes(
      peakRail,
      peakRailFull,
      startOffset,
      endOffset,
      roofFramingSpacing,
      roofFramingSpacingMethod,
      constraints,
    ) ?? []
  );
}

export function getTrussGenerator(trussType: RoofTrussType) {
  switch (trussType) {
    case RoofTrussType.CHORD_TRUSS:
      return new ChordTrussGenerator();
    case RoofTrussType.FLY_TRUSS:
      return new FlyTrussGenerator();
    case RoofTrussType.KING_POST_TRUSS:
      return new KingPostTrussGenerator();
    case RoofTrussType.QUEEN_POST_TRUSS:
      return new QueenPostTrussGenerator();
    case RoofTrussType.FINK_TRUSS:
      return new FinkTrussGenerator();
    case RoofTrussType.HOWE_TRUSS:
      return new HoweTrussGenerator();
    case RoofTrussType.SCISSOR_TRUSS:
      return new ScissorTrussGenerator();
    default:
      throw new Error(`Unsupported truss type: ${trussType}`);
  }
}
const RIDGE_RAIL_CONNECTION_TOLERANCE = TRUSS_THICKNESS; //@TODO: see if this can be reduced to a smaller value. The reason to use this value is that framing planes origin are shifted by TRUSS_THICKNESS

interface RidgeRailEndpoint {
  readonly x: number;
  readonly y: number;
  readonly z: number;
}

interface RidgeRailCheck {
  readonly rail: RidgeRail;
  readonly originPoint: RidgeRailEndpoint;
  readonly normVectors: {
    readonly norm: [number, number, number];
    readonly rightNorm: [number, number, number];
  };
  readonly atStart: boolean;
  readonly isLeftSide: boolean;
}

function checkRidgeRailEndpoint(
  point: RidgeRailEndpoint,
  { originPoint, normVectors, atStart, isLeftSide }: Omit<RidgeRailCheck, "rail">,
): boolean {
  const sideBackFront = vec3PlaneSide(
    point.x,
    point.y,
    point.z,
    originPoint.x,
    originPoint.y,
    originPoint.z,
    ...normVectors.norm,
  );

  if ((atStart && sideBackFront === PlaneSide.BACK) || (!atStart && sideBackFront === PlaneSide.FRONT)) {
    const sideLeftRight = vec3PlaneSide(
      point.x,
      point.y,
      point.z,
      originPoint.x,
      originPoint.y,
      originPoint.z,
      ...normVectors.rightNorm,
    );

    return isLeftSide ? sideLeftRight === PlaneSide.BACK : sideLeftRight === PlaneSide.FRONT;
  }

  return false;
}

function findMatchingRidgeRail(
  ridgeRails: RidgeRail[],
  checkParams: Omit<RidgeRailCheck, "rail">,
): RidgeRail | undefined {
  return ridgeRails.find(({ rail }) => {
    if (Math.abs(rail[2] - rail[5]) < DIST_EPSILON) {
      return false; // skip peak ridge rail
    }

    const dist1 = vec3Dist(
      rail[0],
      rail[1],
      rail[2],
      checkParams.originPoint.x,
      checkParams.originPoint.y,
      checkParams.originPoint.z,
    );
    const dist2 = vec3Dist(
      rail[3],
      rail[4],
      rail[5],
      checkParams.originPoint.x,
      checkParams.originPoint.y,
      checkParams.originPoint.z,
    );

    if (dist1 > RIDGE_RAIL_CONNECTION_TOLERANCE && dist2 > RIDGE_RAIL_CONNECTION_TOLERANCE) {
      return false;
    }
    if (dist1 < RIDGE_RAIL_CONNECTION_TOLERANCE && dist2 < RIDGE_RAIL_CONNECTION_TOLERANCE) {
      return false;
    }

    const point1: RidgeRailEndpoint = { x: rail[0], y: rail[1], z: rail[2] };
    const point2: RidgeRailEndpoint = { x: rail[3], y: rail[4], z: rail[5] };

    return (
      (dist1 < RIDGE_RAIL_CONNECTION_TOLERANCE && checkRidgeRailEndpoint(point2, checkParams)) ||
      (dist2 < RIDGE_RAIL_CONNECTION_TOLERANCE && checkRidgeRailEndpoint(point1, checkParams))
    );
  });
}

function adjustRidgeRailEndpoint(
  rail: PolyLine3,
  originPoint: RidgeRailEndpoint,
  tolerance: number = RIDGE_RAIL_CONNECTION_TOLERANCE,
): PolyLine3 {
  const dist1 = vec3Dist(rail[0], rail[1], rail[2], originPoint.x, originPoint.y, originPoint.z);
  const dist2 = vec3Dist(rail[3], rail[4], rail[5], originPoint.x, originPoint.y, originPoint.z);

  let adjustedRail = polyLine3Copy(rail);

  if (dist1 < tolerance) {
    // Replace start point with origin point
    adjustedRail = polyLine3New([originPoint.x, originPoint.y, originPoint.z, rail[3], rail[4], rail[5]]);
  } else if (dist2 < tolerance) {
    // Replace end point with origin point
    adjustedRail = polyLine3New([rail[0], rail[1], rail[2], originPoint.x, originPoint.y, originPoint.z]);
  }

  return adjustedRail;
}

function getLeftRightRidgeRails(
  ridgeRails: RidgeRail[],
  computedFramingPlanes: GeneratedRoofFramingPlane[],
  atStart: boolean,
  index: number | undefined,
  framingPlaneThickness: number,
): { leftRidgeRail: PolyLine3 | undefined; rightRidgeRail: PolyLine3 | undefined } {
  if (!index || index === computedFramingPlanes.length - 1) {
    return { leftRidgeRail: undefined, rightRidgeRail: undefined };
  }

  const currentPlane = atStart
    ? planeCopyMoveAlongNormal(computedFramingPlanes[index].plane, framingPlaneThickness)
    : computedFramingPlanes[index].plane;
  const nextPlane = computedFramingPlanes[index + 1].plane;

  const originPoint: RidgeRailEndpoint = {
    x: currentPlane.originX,
    y: currentPlane.originY,
    z: currentPlane.originZ,
  };

  const [normX, normY, normZ] = vec3Normalize(
    nextPlane.originX - currentPlane.originX,
    nextPlane.originY - currentPlane.originY,
    nextPlane.originZ - currentPlane.originZ,
  );
  const [rightNormX, rightNormY, rightNormZ] = vec3Cross(normX, normY, normZ, 0, 0, 1);

  const normVectors = {
    norm: [normX, normY, normZ] as [number, number, number],
    rightNorm: [rightNormX, rightNormY, rightNormZ] as [number, number, number],
  };

  const baseParams = {
    originPoint,
    normVectors,
    atStart,
  };

  const leftRidgeRail = findMatchingRidgeRail(ridgeRails, { ...baseParams, isLeftSide: true });
  const rightRidgeRail = findMatchingRidgeRail(ridgeRails, { ...baseParams, isLeftSide: false });
  let adjustedLeftRidgeRail: PolyLine3 | undefined = undefined;
  let adjustedRightRidgeRail: PolyLine3 | undefined = undefined;

  if (leftRidgeRail) {
    adjustedLeftRidgeRail = adjustRidgeRailEndpoint(polyLine3Copy(leftRidgeRail.rail), originPoint);
  }
  if (rightRidgeRail) {
    adjustedRightRidgeRail = adjustRidgeRailEndpoint(polyLine3Copy(rightRidgeRail.rail), originPoint);
  }

  return {
    leftRidgeRail: adjustedLeftRidgeRail,
    rightRidgeRail: adjustedRightRidgeRail,
  };
}

const TOO_HIGH_FOR_RIDGE_RAFTER = 10000000;
/**
 * Adds ridge rafters (left, right, and peak) to the roofFraming array.
 */
function addRidgeRaftersToRoofFraming(params: {
  roofFraming: RoofFraming[];
  ridgeRailsAtPeak: RidgeRailAtPeakParams;
  leftTopChordPitch: number;
  rightTopChordPitch: number;
  ridgeRafterProfile: LumberProfileType;
  trussFramingPlanes: Plane[];
  framingPlaneThickness: number;
}) {
  const {
    roofFraming,
    ridgeRailsAtPeak,
    leftTopChordPitch,
    rightTopChordPitch,
    ridgeRafterProfile,
    trussFramingPlanes,
    framingPlaneThickness,
  } = params;

  const {
    leftRidgeRailAtFirstPeak,
    rightRidgeRailAtFirstPeak,
    leftRidgeRailAtLastPeak,
    rightRidgeRailAtLastPeak,
    peakRidgeRail,
    firstPeakFramingPlaneIndex,
    lastPeakFramingPlaneIndex,
  } = ridgeRailsAtPeak;

  const topClipPlane = planeNewXY(0, 0, peakRidgeRail ? peakRidgeRail[2] : TOO_HIGH_FOR_RIDGE_RAFTER);
  const clipPlanesForRidgesAtFirstPeak: Plane[] = [topClipPlane];
  const clipPlanesForRidgesAtLastPeak: Plane[] = [topClipPlane];
  const clipPlanesForRidgesAtPeak: Plane[] = [];

  if (leftRidgeRailAtFirstPeak || rightRidgeRailAtFirstPeak) {
    clipPlanesForRidgesAtFirstPeak.push(planeCopy(trussFramingPlanes[0]));
    if (firstPeakFramingPlaneIndex) {
      clipPlanesForRidgesAtFirstPeak.push(
        planeReverse(planeCopyMoveAlongNormal(trussFramingPlanes[firstPeakFramingPlaneIndex], framingPlaneThickness)),
      );
    }
  }
  if (leftRidgeRailAtLastPeak || rightRidgeRailAtLastPeak) {
    clipPlanesForRidgesAtLastPeak.push(
      planeReverse(planeCopyMoveAlongNormal(trussFramingPlanes[trussFramingPlanes.length - 1], framingPlaneThickness)),
    );
    if (lastPeakFramingPlaneIndex) {
      clipPlanesForRidgesAtLastPeak.push(planeCopy(trussFramingPlanes[lastPeakFramingPlaneIndex]));
    }
  }

  if (peakRidgeRail) {
    if (leftRidgeRailAtFirstPeak || rightRidgeRailAtFirstPeak) {
      if (firstPeakFramingPlaneIndex) {
        clipPlanesForRidgesAtPeak.push(planeCopy(trussFramingPlanes[firstPeakFramingPlaneIndex]));
      }
    } else {
      clipPlanesForRidgesAtPeak.push(planeCopyMoveAlongNormal(trussFramingPlanes[0], framingPlaneThickness));
    }
    if (leftRidgeRailAtLastPeak || rightRidgeRailAtLastPeak) {
      if (lastPeakFramingPlaneIndex) {
        clipPlanesForRidgesAtPeak.push(
          planeReverse(planeCopyMoveAlongNormal(trussFramingPlanes[lastPeakFramingPlaneIndex], framingPlaneThickness)),
        );
      }
    } else {
      clipPlanesForRidgesAtPeak.push(planeCopyReversed(trussFramingPlanes[trussFramingPlanes.length - 1]));
    }
  }

  if (leftRidgeRailAtFirstPeak) {
    const leftRidgeRafter = generateRidgeRafter(
      leftRidgeRailAtFirstPeak,
      ridgeRafterProfile,
      clipPlanesForRidgesAtFirstPeak,
      false,
      unsignedNumberNew(leftTopChordPitch),
      unsignedNumberNew(rightTopChordPitch),
    );
    roofFraming.push(leftRidgeRafter);
  }

  if (rightRidgeRailAtFirstPeak) {
    const rightRidgeRafter = generateRidgeRafter(
      rightRidgeRailAtFirstPeak,
      ridgeRafterProfile,
      clipPlanesForRidgesAtFirstPeak,
      false,
      unsignedNumberNew(leftTopChordPitch),
      unsignedNumberNew(rightTopChordPitch),
    );
    roofFraming.push(rightRidgeRafter);
  }

  if (leftRidgeRailAtLastPeak) {
    const leftRidgeRafter = generateRidgeRafter(
      leftRidgeRailAtLastPeak,
      ridgeRafterProfile,
      clipPlanesForRidgesAtLastPeak,
      false,
      unsignedNumberNew(leftTopChordPitch),
      unsignedNumberNew(rightTopChordPitch),
    );
    roofFraming.push(leftRidgeRafter);
  }

  if (rightRidgeRailAtLastPeak) {
    const rightRidgeRafter = generateRidgeRafter(
      rightRidgeRailAtLastPeak,
      ridgeRafterProfile,
      clipPlanesForRidgesAtLastPeak,
      false,
      unsignedNumberNew(leftTopChordPitch),
      unsignedNumberNew(rightTopChordPitch),
    );
    roofFraming.push(rightRidgeRafter);
  }

  if (peakRidgeRail) {
    const peakRidgeRafter = generateRidgeRafter(
      peakRidgeRail,
      ridgeRafterProfile,
      clipPlanesForRidgesAtPeak,
      true,
      unsignedNumberNew(leftTopChordPitch),
      unsignedNumberNew(rightTopChordPitch),
    );
    roofFraming.push(peakRidgeRafter);
  }
}

function getFirstLastPeakFramingPlaneIndices(computedFramingPlanes: GeneratedRoofFramingPlane[]): {
  firstPeakFramingPlaneIndex: number | undefined;
  lastPeakFramingPlaneIndex: number | undefined;
} {
  let firstPeakFramingPlaneIndex: number | undefined = undefined;
  let lastPeakFramingPlaneIndex: number | undefined = undefined;
  for (let i = 0; i < computedFramingPlanes.length; i++) {
    const element = computedFramingPlanes[i];

    if (firstPeakFramingPlaneIndex === undefined) {
      if (element.type === RoofFramingPlaneType.STICK_FRAME_PLANE_AT_START) {
        firstPeakFramingPlaneIndex = i;
      }
      if (element.type === RoofFramingPlaneType.PEAK_FRAMING_PLANE && i > 0) {
        const previousElement = computedFramingPlanes[i - 1];
        if (
          previousElement.type === RoofFramingPlaneType.FLY_RAFTER_PLANE_AT_START ||
          previousElement.type === RoofFramingPlaneType.FLY_RAFTER_PLANE_AT_INTERIM ||
          previousElement.type === RoofFramingPlaneType.HIP_FRAMING_PLANE
        ) {
          firstPeakFramingPlaneIndex = i;
        }
      }
    }
    if (lastPeakFramingPlaneIndex === undefined) {
      if (element.type === RoofFramingPlaneType.STICK_FRAME_PLANE_AT_END) {
        lastPeakFramingPlaneIndex = i;
      }
      if (element.type === RoofFramingPlaneType.PEAK_FRAMING_PLANE && i < computedFramingPlanes.length - 1) {
        const nextElement = computedFramingPlanes[i + 1];
        if (
          nextElement.type === RoofFramingPlaneType.FLY_RAFTER_PLANE_AT_END ||
          nextElement.type === RoofFramingPlaneType.FLY_RAFTER_PLANE_AT_INTERIM ||
          nextElement.type === RoofFramingPlaneType.HIP_FRAMING_PLANE
        ) {
          lastPeakFramingPlaneIndex = i;
        }
      }
    }
    if (firstPeakFramingPlaneIndex !== undefined && lastPeakFramingPlaneIndex !== undefined) {
      break;
    }
  }
  return { firstPeakFramingPlaneIndex, lastPeakFramingPlaneIndex };
}

function addLookoutRaftersToRoofFraming(params: {
  roofFraming: RoofFraming[];
  lookoutRafterProfile: LumberProfileType;
  computedFramingPlanes: GeneratedRoofFramingPlane[];
  ridgeRailsAtPeak: RidgeRailAtPeakParams;
  roofFramingSpacing: number;
  leftSpan: number;
  rightSpan: number;
  leftOverhang: number;
  rightOverhang: number;
  leftTopChordPitch: number;
  rightTopChordPitch: number;
  lookoutRafterSpacing: number;
  includeLookoutRaftersAtStart: boolean;
  includeLookoutRaftersAtEnd: boolean;
}) {
  const {
    roofFraming,
    lookoutRafterProfile,
    computedFramingPlanes,
    ridgeRailsAtPeak,
    roofFramingSpacing,
    leftSpan,
    rightSpan,
    leftOverhang,
    rightOverhang,
    leftTopChordPitch,
    rightTopChordPitch,
    lookoutRafterSpacing,
    includeLookoutRaftersAtStart,
    includeLookoutRaftersAtEnd,
  } = params;

  const { firstPeakFramingPlaneIndex, lastPeakFramingPlaneIndex } = ridgeRailsAtPeak;

  const startRafterFramingPlane = computedFramingPlanes.find(
    ({ type }) => type === RoofFramingPlaneType.FLY_RAFTER_PLANE_AT_START,
  )?.plane;
  const endRafterFramingPlane = computedFramingPlanes.find(
    ({ type }) => type === RoofFramingPlaneType.FLY_RAFTER_PLANE_AT_END,
  )?.plane;
  if (startRafterFramingPlane && firstPeakFramingPlaneIndex !== undefined && includeLookoutRaftersAtStart) {
    const lengthLookoutStart = planeDistanceTo(
      startRafterFramingPlane,
      computedFramingPlanes[firstPeakFramingPlaneIndex + 1].plane.originX,
      computedFramingPlanes[firstPeakFramingPlaneIndex + 1].plane.originY,
      computedFramingPlanes[firstPeakFramingPlaneIndex + 1].plane.originZ,
    );
    if (lengthLookoutStart > 0.5 * roofFramingSpacing) {
      //generate lookout at start
      const lookoutStart = generateLookout(
        unsignedNumberNew(leftSpan),
        unsignedNumberNew(rightSpan),
        unsignedNumberNew(leftOverhang),
        unsignedNumberNew(rightOverhang),
        unsignedNumberNew(leftTopChordPitch),
        unsignedNumberNew(rightTopChordPitch),
        unsignedNumberNew(lengthLookoutStart),
        unsignedNumberNew(lookoutRafterSpacing),
        lookoutRafterProfile,
        planeCopy(startRafterFramingPlane),
      );
      roofFraming.push(lookoutStart);
    }
  }
  if (endRafterFramingPlane && lastPeakFramingPlaneIndex !== undefined && includeLookoutRaftersAtEnd) {
    const lengthLookoutEnd = planeDistanceTo(
      endRafterFramingPlane,
      computedFramingPlanes[lastPeakFramingPlaneIndex - 1].plane.originX,
      computedFramingPlanes[lastPeakFramingPlaneIndex - 1].plane.originY,
      computedFramingPlanes[lastPeakFramingPlaneIndex - 1].plane.originZ,
    );
    if (lengthLookoutEnd > 0.5 * roofFramingSpacing) {
      //generate lookout at end
      const lookoutEnd = generateLookout(
        unsignedNumberNew(leftSpan),
        unsignedNumberNew(rightSpan),
        unsignedNumberNew(leftOverhang),
        unsignedNumberNew(rightOverhang),
        unsignedNumberNew(leftTopChordPitch),
        unsignedNumberNew(rightTopChordPitch),
        unsignedNumberNew(lengthLookoutEnd),
        unsignedNumberNew(lookoutRafterSpacing),
        lookoutRafterProfile,
        planeCopy(computedFramingPlanes[lastPeakFramingPlaneIndex - 1].plane),
      );
      roofFraming.push(lookoutEnd);
    }
  }
}

interface TrussGenerationParams {
  readonly trussBasicInput: RoofTrussBasicInput;
  readonly leftSpan: number;
  readonly rightSpan: number;
  readonly leftOverhang: number;
  readonly rightOverhang: number;
  readonly leftTopChordPitch: number;
  readonly rightTopChordPitch: number;
  readonly ceilingJoistOffset: number;
  readonly ridgeRafterProfile: LumberProfileType;
  readonly trussPlane: Plane;
}

interface StickFramingConfigParams {
  readonly topChordProfile: LumberProfileType;
  readonly bottomChordProfile: LumberProfileType;
  readonly webChordProfile: LumberProfileType;
  readonly webChordSpacing: number;
  readonly additionalPlaneOffset: number;
}

interface StickFramingParams extends TrussGenerationParams {
  readonly index: number;
  readonly framingPlanes: GeneratedRoofFramingPlane[];
  readonly config: StickFramingConfigParams;
  readonly lookoutRafterProfile: LumberProfileType;
}

interface FlyRafterParams extends TrussGenerationParams {
  readonly flyRafterProfile: LumberProfileType;
}

interface HipTrussParams extends TrussGenerationParams {
  readonly index: number;
  readonly framingPlanes: GeneratedRoofFramingPlane[];
  readonly ridgeRailsAtPeak: RidgeRailAtPeakParams;
  readonly peakRailExtendedNorm: [number, number, number];
  readonly topChordProfile: LumberProfileType;
  readonly bottomChordProfile: LumberProfileType;
  readonly webChordProfile: LumberProfileType;
}

function generateStickFrameTruss(
  generator: StickFrameTrussGenerator,
  params: StickFramingParams,
): RoofTruss | undefined {
  const {
    trussBasicInput,
    leftSpan,
    rightSpan,
    leftOverhang,
    rightOverhang,
    leftTopChordPitch,
    rightTopChordPitch,
    ceilingJoistOffset,
    ridgeRafterProfile,
    trussPlane,
    config,
    lookoutRafterProfile,
  } = params;

  const {
    topChordProfile,
    bottomChordProfile,
    webChordProfile,
    webChordSpacing: webChordHorizontalSpacing,
    additionalPlaneOffset,
  } = config;

  return generator.generate({
    ...trussBasicInput,
    leftSpan: unsignedNumberNew(leftSpan),
    rightSpan: unsignedNumberNew(rightSpan),
    leftOverhang: unsignedNumberNew(leftOverhang),
    rightOverhang: unsignedNumberNew(rightOverhang),
    leftTopChordPitch: unsignedNumberNew(leftTopChordPitch),
    rightTopChordPitch: unsignedNumberNew(rightTopChordPitch),
    bottomChordOffset: ceilingJoistOffset,
    trussType: RoofTrussType.STICK_FRAME_TRUSS,
    ridgeRafterProfile,
    topChordProfile,
    bottomChordProfile,
    webChordProfile,
    webChordHorizontalSpacing: unsignedNumberNew(webChordHorizontalSpacing),
    trussPlane,
    lookoutProfile: lookoutRafterProfile,
    additionalPlaneOffset,
  }) as RoofTruss;
}

function generateFlyRafterTruss(generator: FlyTrussGenerator, params: FlyRafterParams): RoofTruss | undefined {
  const {
    trussBasicInput,
    leftSpan,
    rightSpan,
    leftOverhang,
    rightOverhang,
    leftTopChordPitch,
    rightTopChordPitch,
    ceilingJoistOffset,
    ridgeRafterProfile,
    trussPlane,
    flyRafterProfile,
  } = params;

  return generator.generate({
    ...trussBasicInput,
    leftSpan: unsignedNumberNew(leftSpan),
    rightSpan: unsignedNumberNew(rightSpan),
    leftOverhang: unsignedNumberNew(leftOverhang),
    rightOverhang: unsignedNumberNew(rightOverhang),
    leftTopChordPitch: unsignedNumberNew(leftTopChordPitch),
    rightTopChordPitch: unsignedNumberNew(rightTopChordPitch),
    bottomChordOffset: ceilingJoistOffset,
    trussType: RoofTrussType.FLY_TRUSS,
    ridgeRafterProfile,
    topChordProfile: flyRafterProfile,
    trussPlane,
  }) as RoofTruss;
}

function generatePeakTruss(
  generator: ReturnType<typeof getTrussGenerator>,
  params: TrussGenerationParams & {
    trussType: RoofTrussType;
    topChordProfile: LumberProfileType;
    bottomChordProfile: LumberProfileType;
    webChordProfile: LumberProfileType;
  },
): RoofTruss | undefined {
  const {
    trussBasicInput,
    leftSpan,
    rightSpan,
    leftOverhang,
    rightOverhang,
    leftTopChordPitch,
    rightTopChordPitch,
    ceilingJoistOffset,
    ridgeRafterProfile,
    trussPlane,
    trussType,
    topChordProfile,
    bottomChordProfile,
    webChordProfile,
  } = params;

  return generator.generate({
    ...trussBasicInput,
    leftSpan: unsignedNumberNew(leftSpan),
    rightSpan: unsignedNumberNew(rightSpan),
    leftOverhang: unsignedNumberNew(leftOverhang),
    rightOverhang: unsignedNumberNew(rightOverhang),
    leftTopChordPitch: unsignedNumberNew(leftTopChordPitch),
    rightTopChordPitch: unsignedNumberNew(rightTopChordPitch),
    bottomChordOffset: ceilingJoistOffset,
    trussType,
    ridgeRafterProfile,
    topChordProfile,
    bottomChordProfile,
    webChordProfile,
    trussPlane,
  }) as RoofTruss;
}

function generateHipTruss(generator: HipTrussGenerator, params: HipTrussParams): RoofTruss | undefined {
  const {
    index,
    framingPlanes,
    ridgeRailsAtPeak,
    peakRailExtendedNorm,
    trussBasicInput,
    leftSpan,
    rightSpan,
    leftTopChordPitch,
    rightTopChordPitch,
    ceilingJoistOffset,
    ridgeRafterProfile,
    trussPlane,
    topChordProfile,
    bottomChordProfile,
    webChordProfile,
    leftOverhang,
    rightOverhang,
  } = params;

  let spans: { leftHipSpan: number | undefined; rightHipSpan: number | undefined };

  const {
    firstPeakFramingPlaneIndex,
    lastPeakFramingPlaneIndex,
    leftRidgeRailAtFirstPeak,
    rightRidgeRailAtFirstPeak,
    leftRidgeRailAtLastPeak,
    rightRidgeRailAtLastPeak,
  } = ridgeRailsAtPeak;

  if (
    firstPeakFramingPlaneIndex &&
    index < firstPeakFramingPlaneIndex &&
    leftRidgeRailAtFirstPeak &&
    rightRidgeRailAtFirstPeak
  ) {
    spans = calculateHipSpans(
      index,
      framingPlanes,
      firstPeakFramingPlaneIndex,
      leftRidgeRailAtFirstPeak,
      rightRidgeRailAtFirstPeak,
      peakRailExtendedNorm,
    );
  } else if (
    lastPeakFramingPlaneIndex &&
    index > lastPeakFramingPlaneIndex &&
    leftRidgeRailAtLastPeak &&
    rightRidgeRailAtLastPeak
  ) {
    spans = calculateHipSpans(
      index,
      framingPlanes,
      lastPeakFramingPlaneIndex,
      leftRidgeRailAtLastPeak,
      rightRidgeRailAtLastPeak,
      peakRailExtendedNorm,
    );
  } else {
    return undefined;
  }

  if (!spans.leftHipSpan || !spans.rightHipSpan) {
    return undefined;
  }

  return generator.generate({
    ...trussBasicInput,
    leftSpan: [
      {
        span: unsignedNumberNew(leftSpan - spans.leftHipSpan),
        pitch: unsignedNumberNew(leftTopChordPitch),
        profile: topChordProfile,
      },
      {
        span: unsignedNumberNew(spans.leftHipSpan),
        pitch: unsignedNumberNew(0),
        profile: topChordProfile,
      },
    ],
    rightSpan: [
      {
        span: unsignedNumberNew(rightSpan - spans.rightHipSpan),
        pitch: unsignedNumberNew(rightTopChordPitch),
        profile: topChordProfile,
      },
      {
        span: unsignedNumberNew(spans.rightHipSpan),
        pitch: unsignedNumberNew(0),
        profile: topChordProfile,
      },
    ],
    bottomChordOffset: ceilingJoistOffset,
    trussType: RoofTrussType.HIP_TRUSS,
    ridgeRafterProfile,
    bottomChordProfile,
    webChordProfile,
    trussPlane,
    leftOverhang: unsignedNumberNew(leftOverhang),
    rightOverhang: unsignedNumberNew(rightOverhang),
  }) as RoofTruss;
}

function generateTrussForPlane(
  framingPlane: GeneratedRoofFramingPlane,
  index: number,
  params: {
    stickFrameTrussGenerator?: StickFrameTrussGenerator;
    flyTrussGenerator?: FlyTrussGenerator;
    peakRailTrussGenerator: ReturnType<typeof getTrussGenerator>;
    hipTrussGenerator?: HipTrussGenerator;
    includeFlyRaftersAtStart: boolean;
    includeFlyRaftersAtEnd: boolean;
    hipFramingMethod: HipFramingMethod;
    trussBasicInput: RoofTrussBasicInput;
    leftSpan: number;
    rightSpan: number;
    leftOverhang: number;
    rightOverhang: number;
    ceilingJoistOffset: number;
    ridgeRafterProfile: LumberProfileType;
    topChordProfile: LumberProfileType;
    bottomChordProfile: LumberProfileType;
    webChordProfile: LumberProfileType;
    flyRafterProfile: LumberProfileType;
    lookoutRafterProfile: LumberProfileType;
    start: StickFramingConfigParams & { include: boolean };
    end: StickFramingConfigParams & { include: boolean };
    trussType: RoofTrussType;
    framingPlanes: GeneratedRoofFramingPlane[];
    ridgeRailsAtPeak: RidgeRailAtPeakParams;
    peakRailExtendedNorm: [number, number, number];
    setBackForStepDownTruss: number;
    framingPlaneThickness: number;
    roofSlopes: RoofSlopesParams; //TODO: this should eventually be replaced with a function that returns the roof slopes for the given framing plane, similar to computeSpanAt below
    jackRafterProfile: LumberProfileType;
    fasciaSizes: FasciaSizeParams; //TODO: this should eventually be replaced with a function that returns the fascia sizes for the given framing plane, similar to computeSpanAt below
    roofFraming: RoofFraming[];
    startHipFramingPlaneIndex: number | undefined;
    endHipFramingPlaneIndex: number | undefined;
    computeSpanAt: (x: number, y: number, vec: Vec2) => number;
  },
) {
  const { plane: trussPlane, type: regionType } = framingPlane;
  const baseParams: TrussGenerationParams = {
    trussBasicInput: params.trussBasicInput,
    leftSpan: params.leftSpan,
    rightSpan: params.rightSpan,
    leftOverhang: params.leftOverhang,
    rightOverhang: params.rightOverhang,
    leftTopChordPitch: params.roofSlopes.leftTopChordPitch,
    rightTopChordPitch: params.roofSlopes.rightTopChordPitch,
    ceilingJoistOffset: params.ceilingJoistOffset,
    ridgeRafterProfile: params.ridgeRafterProfile,
    trussPlane,
  };

  if (params.stickFrameTrussGenerator) {
    if (regionType === RoofFramingPlaneType.STICK_FRAME_PLANE_AT_START && params.start.include) {
      const truss = generateStickFrameTruss(params.stickFrameTrussGenerator, {
        ...baseParams,
        index,
        config: params.start,
        lookoutRafterProfile: params.lookoutRafterProfile,
        framingPlanes: params.framingPlanes,
      });

      if (truss) {
        params.roofFraming.push(truss);
      }
      return;
    } else if (regionType === RoofFramingPlaneType.STICK_FRAME_PLANE_AT_END && params.end.include) {
      const truss = generateStickFrameTruss(params.stickFrameTrussGenerator, {
        ...baseParams,
        index,
        config: params.end,
        lookoutRafterProfile: params.lookoutRafterProfile,
        framingPlanes: params.framingPlanes,
      });
      if (truss) {
        params.roofFraming.push(truss);
      }
      return;
    }
  }

  if (
    params.flyTrussGenerator &&
    ((params.includeFlyRaftersAtStart && regionType === RoofFramingPlaneType.FLY_RAFTER_PLANE_AT_START) ||
      (params.includeFlyRaftersAtEnd && regionType === RoofFramingPlaneType.FLY_RAFTER_PLANE_AT_END))
  ) {
    const truss = generateFlyRafterTruss(params.flyTrussGenerator, {
      ...baseParams,
      flyRafterProfile: params.flyRafterProfile,
    });

    if (truss) {
      params.roofFraming.push(truss);
    }
    return;
  }

  if (
    params.peakRailTrussGenerator &&
    (regionType === RoofFramingPlaneType.PEAK_FRAMING_PLANE ||
      (regionType === RoofFramingPlaneType.STICK_FRAME_PLANE_AT_START && !params.start.include) ||
      (regionType === RoofFramingPlaneType.STICK_FRAME_PLANE_AT_END && !params.end.include))
  ) {
    const truss = generatePeakTruss(params.peakRailTrussGenerator, {
      ...baseParams,
      trussType: params.trussType,
      topChordProfile: params.topChordProfile,
      bottomChordProfile: params.bottomChordProfile,
      webChordProfile: params.webChordProfile,
      leftSpan: params.computeSpanAt(trussPlane.originX, trussPlane.originY, [trussPlane.uX, trussPlane.uY]),
      rightSpan: params.computeSpanAt(trussPlane.originX, trussPlane.originY, [-trussPlane.uX, -trussPlane.uY]),
    });

    if (truss) {
      params.roofFraming.push(truss);
    }
    return;
  }

  if (
    params.hipTrussGenerator &&
    regionType === RoofFramingPlaneType.HIP_FRAMING_PLANE &&
    index !== 0 &&
    index !== params.framingPlanes.length - 1 &&
    params.startHipFramingPlaneIndex !== undefined &&
    params.endHipFramingPlaneIndex !== undefined
  ) {
    if (
      index >= params.startHipFramingPlaneIndex &&
      index <= params.endHipFramingPlaneIndex &&
      params.hipFramingMethod !== HipFramingMethod.JACK_RAFTERS
    ) {
      switch (params.hipFramingMethod) {
        case HipFramingMethod.STEP_DOWN_HIP_FRAMING: {
          const truss = generateHipTruss(params.hipTrussGenerator, {
            ...baseParams,
            index,
            framingPlanes: params.framingPlanes,
            ridgeRailsAtPeak: params.ridgeRailsAtPeak,
            peakRailExtendedNorm: params.peakRailExtendedNorm,
            topChordProfile: params.topChordProfile,
            bottomChordProfile: params.bottomChordProfile,
            webChordProfile: params.webChordProfile,
            leftOverhang: params.leftOverhang,
            rightOverhang: params.rightOverhang,
          });

          if (truss) {
            params.roofFraming.push(truss);
          }
          break;
        }
        default: {
          break;
        }
      }
    } else {
      generateJackRafters(framingPlane, index, params.roofFraming, {
        framingPlanes: params.framingPlanes,
        jackRafterProfile: params.jackRafterProfile,
        ridgeRailsAtPeak: params.ridgeRailsAtPeak,
        roofSlopes: params.roofSlopes,
        leftSpan: unsignedNumberNew(
          params.computeSpanAt(framingPlane.plane.originX, framingPlane.plane.originY, [
            framingPlane.plane.uX,
            framingPlane.plane.uY,
          ]),
        ),
        rightSpan: unsignedNumberNew(
          params.computeSpanAt(framingPlane.plane.originX, framingPlane.plane.originY, [
            -framingPlane.plane.uX,
            -framingPlane.plane.uY,
          ]),
        ),
        peakRailExtendedNorm: params.peakRailExtendedNorm,
        fasciaSizes: params.fasciaSizes,
      });
    }
  }
}

function hipFramingPlanesStartEndIndices(
  framingPlanes: GeneratedRoofFramingPlane[],
  startSetBackForHipTruss: number,
  endSetBackForHipTruss: number,
  framingPlaneThickness: number,
): {
  startHipFramingPlaneIndex: number | undefined;
  endHipFramingPlaneIndex: number | undefined;
} {
  let startHipFramingPlaneIndex: number | undefined;
  let endHipFramingPlaneIndex: number | undefined;

  for (let i = 0; i < framingPlanes.length; i++) {
    const framingPlane = framingPlanes[i];
    if (framingPlane.type === RoofFramingPlaneType.HIP_FRAMING_PLANE) {
      const currentDistanceFromStart =
        vec3Dist(
          framingPlane.plane.originX,
          framingPlane.plane.originY,
          framingPlane.plane.originZ,
          framingPlanes[0].plane.originX,
          framingPlanes[0].plane.originY,
          framingPlanes[0].plane.originZ,
        ) -
        framingPlaneThickness -
        DIST_EPSILON;

      if (currentDistanceFromStart > startSetBackForHipTruss && !startHipFramingPlaneIndex) {
        startHipFramingPlaneIndex = i;
      }

      const currentDistanceFromEnd =
        vec3Dist(
          framingPlane.plane.originX,
          framingPlane.plane.originY,
          framingPlane.plane.originZ,
          framingPlanes[framingPlanes.length - 1].plane.originX,
          framingPlanes[framingPlanes.length - 1].plane.originY,
          framingPlanes[framingPlanes.length - 1].plane.originZ,
        ) -
        framingPlaneThickness -
        DIST_EPSILON;

      if (currentDistanceFromEnd < endSetBackForHipTruss && !endHipFramingPlaneIndex && i !== 0) {
        endHipFramingPlaneIndex = i - 1;
      }

      if (startHipFramingPlaneIndex && endHipFramingPlaneIndex) {
        break;
      }
    }
  }

  return {
    startHipFramingPlaneIndex,
    endHipFramingPlaneIndex,
  };
}
